import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Building2,
  Calculator,
  TrendingUp,
  Eye,
  Check,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface HealthCheckResult {
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  totalConfigs: number;
  healthyConfigs: number;
  warningConfigs: number;
  criticalConfigs: number;
  issues: HealthIssue[];
  recommendations: Recommendation[];
  lastChecked: string;
}

interface HealthIssue {
  id: string;
  type: 'NEGATIVE_MARGIN' | 'BELOW_MINIMUM' | 'MISSING_CONFIG' | 'INACTIVE_CONFIG';
  severity: 'WARNING' | 'CRITICAL';
  entityType: 'PLATFORM' | 'TENANT';
  entityId?: string;
  entityName?: string;
  operationType: string;
  message: string;
  currentValue?: number;
  expectedValue?: number;
  createdAt: string;
  resolvedAt?: string;
}

interface Recommendation {
  operationType: string;
  entityType: 'PLATFORM' | 'TENANT';
  entityId?: string;
  entityName?: string;
  currentFee: {
    fixedFee: number;
    percentageFee: number;
  };
  recommendedFee: {
    fixedFee: number;
    percentageFee: number;
  };
  reason: string;
}

interface FeeAlert {
  id: string;
  type: string;
  severity: 'WARNING' | 'CRITICAL';
  message: string;
  entityType: string;
  entityId?: string;
  operationType: string;
  isResolved: boolean;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

const OPERATION_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferência',
  BOLETO: 'Boleto',
  CARD: 'Cartão',
  WITHDRAWAL: 'Saque',
  MAINTENANCE: 'Manutenção',
};

export function FeeHealthCheck() {
  const queryClient = useQueryClient();
  const [selectedIssue, setSelectedIssue] = useState<HealthIssue | null>(null);
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);

  // Fetch health check results
  const { data: healthCheck, isLoading, refetch } = useQuery<HealthCheckResult>({
    queryKey: ['fee-health-check'],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.feeHealth.check);
      return res.data;
    },
  });

  // Fetch alerts
  const { data: alertsData } = useQuery({
    queryKey: ['fee-alerts'],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.feeHealth.alerts);
      return res.data;
    },
  });

  // Extract alerts array from response
  const alerts: FeeAlert[] = alertsData?.alerts || alertsData?.data || (Array.isArray(alertsData) ? alertsData : []);

  // Run health check mutation
  const runCheckMutation = useMutation({
    mutationFn: async () => {
      const res = await api.get(ENDPOINTS.feeHealth.runCheck);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-health-check'] });
      queryClient.invalidateQueries({ queryKey: ['fee-alerts'] });
    },
  });

  // Resolve alert mutation
  const resolveAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      const res = await api.put(ENDPOINTS.feeHealth.resolveAlert(alertId));
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['fee-health-check'] });
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return 'text-green-400';
      case 'WARNING':
        return 'text-yellow-400';
      case 'CRITICAL':
        return 'text-red-400';
      default:
        return 'text-surface-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'WARNING':
        return <AlertTriangle className="w-6 h-6 text-yellow-400" />;
      case 'CRITICAL':
        return <XCircle className="w-6 h-6 text-red-400" />;
      default:
        return <Activity className="w-6 h-6 text-surface-400" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <Badge variant="error">Crítico</Badge>;
      case 'WARNING':
        return <Badge variant="warning">Atenção</Badge>;
      default:
        return <Badge variant="info">{severity}</Badge>;
    }
  };

  const unresolvedAlerts = alerts?.filter((a) => !a.isResolved) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Health Check de Taxas</h1>
          <p className="text-surface-400 mt-1">
            Monitore a saúde das configurações de taxas em toda a plataforma
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={() => runCheckMutation.mutate()}
            disabled={runCheckMutation.isPending}
          >
            {runCheckMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Activity className="w-4 h-4 mr-2" />
            )}
            Executar Verificação
          </Button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Status Geral</p>
              <p className={`text-2xl font-bold mt-1 ${getStatusColor(healthCheck?.status || 'HEALTHY')}`}>
                {healthCheck?.status || 'Verificando...'}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                Última verificação: {healthCheck?.lastChecked 
                  ? new Date(healthCheck.lastChecked).toLocaleString('pt-BR')
                  : '-'}
              </p>
            </div>
            <div className="p-2 bg-surface-800 rounded-lg">
              {getStatusIcon(healthCheck?.status || 'HEALTHY')}
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Configurações Saudáveis</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {healthCheck?.healthyConfigs || 0}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                de {healthCheck?.totalConfigs || 0} total
              </p>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Avisos</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {healthCheck?.warningConfigs || 0}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                configurações com atenção
              </p>
            </div>
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Críticos</p>
              <p className="text-2xl font-bold text-red-400 mt-1">
                {healthCheck?.criticalConfigs || 0}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                requerem ação imediata
              </p>
            </div>
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Active Alerts */}
      {unresolvedAlerts.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Alertas Ativos ({unresolvedAlerts.length})
            </h3>
          </div>
          <div className="space-y-3">
            {unresolvedAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border ${
                  alert.severity === 'CRITICAL'
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {alert.severity === 'CRITICAL' ? (
                      <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{alert.message}</span>
                        {getSeverityBadge(alert.severity)}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-surface-400">
                        <span>{OPERATION_LABELS[alert.operationType] || alert.operationType}</span>
                        <span>•</span>
                        <span>{alert.entityType === 'TENANT' ? 'Tenant' : 'Plataforma'}</span>
                        <span>•</span>
                        <span>{new Date(alert.createdAt).toLocaleString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resolveAlertMutation.mutate(alert.id)}
                    disabled={resolveAlertMutation.isPending}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Resolver
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Issues List */}
      {healthCheck?.issues && healthCheck.issues.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Problemas Detectados ({healthCheck.issues.length})
            </h3>
          </div>
          <div className="space-y-3">
            {healthCheck.issues.map((issue) => (
              <div
                key={issue.id}
                className="p-4 bg-surface-800/50 rounded-lg hover:bg-surface-800 transition-colors cursor-pointer"
                onClick={() => setSelectedIssue(issue)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {issue.severity === 'CRITICAL' ? (
                      <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{issue.message}</span>
                        {getSeverityBadge(issue.severity)}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-surface-400">
                        <span>{OPERATION_LABELS[issue.operationType] || issue.operationType}</span>
                        {issue.entityName && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {issue.entityName}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recommendations */}
      {healthCheck?.recommendations && healthCheck.recommendations.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-400" />
              Recomendações ({healthCheck.recommendations.length})
            </h3>
          </div>
          <div className="space-y-3">
            {healthCheck.recommendations.map((rec, index) => (
              <div
                key={index}
                className="p-4 bg-primary-500/10 border border-primary-500/30 rounded-lg hover:bg-primary-500/20 transition-colors cursor-pointer"
                onClick={() => setSelectedRecommendation(rec)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-primary-400" />
                      <span className="font-medium text-white">
                        {OPERATION_LABELS[rec.operationType] || rec.operationType}
                      </span>
                      {rec.entityName && (
                        <Badge variant="info">{rec.entityName}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-surface-400 mt-1">{rec.reason}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-surface-500">
                        Atual: {formatCurrency(rec.currentFee.fixedFee)} + {rec.currentFee.percentageFee}%
                      </span>
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span className="text-green-400">
                        Sugerido: {formatCurrency(rec.recommendedFee.fixedFee)} + {rec.recommendedFee.percentageFee}%
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && healthCheck?.status === 'HEALTHY' && healthCheck.issues.length === 0 && (
        <Card className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Todas as configurações estão saudáveis!
          </h3>
          <p className="text-surface-400">
            Não foram detectados problemas nas configurações de taxas.
          </p>
        </Card>
      )}

      {/* Issue Detail Modal */}
      <Modal
        isOpen={!!selectedIssue}
        onClose={() => setSelectedIssue(null)}
        title="Detalhes do Problema"
      >
        {selectedIssue && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {selectedIssue.severity === 'CRITICAL' ? (
                <XCircle className="w-5 h-5 text-red-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              )}
              <span className="font-medium text-white">{selectedIssue.message}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-surface-400">Tipo:</span>
                <p className="text-white">{selectedIssue.type}</p>
              </div>
              <div>
                <span className="text-surface-400">Severidade:</span>
                <p>{getSeverityBadge(selectedIssue.severity)}</p>
              </div>
              <div>
                <span className="text-surface-400">Operação:</span>
                <p className="text-white">
                  {OPERATION_LABELS[selectedIssue.operationType] || selectedIssue.operationType}
                </p>
              </div>
              <div>
                <span className="text-surface-400">Entidade:</span>
                <p className="text-white">
                  {selectedIssue.entityName || selectedIssue.entityType}
                </p>
              </div>
              {selectedIssue.currentValue !== undefined && (
                <div>
                  <span className="text-surface-400">Valor Atual:</span>
                  <p className="text-red-400">{formatCurrency(selectedIssue.currentValue)}</p>
                </div>
              )}
              {selectedIssue.expectedValue !== undefined && (
                <div>
                  <span className="text-surface-400">Valor Esperado:</span>
                  <p className="text-green-400">{formatCurrency(selectedIssue.expectedValue)}</p>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-surface-700">
              <p className="text-surface-400 text-sm">
                Detectado em: {new Date(selectedIssue.createdAt).toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Recommendation Detail Modal */}
      <Modal
        isOpen={!!selectedRecommendation}
        onClose={() => setSelectedRecommendation(null)}
        title="Detalhes da Recomendação"
      >
        {selectedRecommendation && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-primary-400" />
              <span className="font-medium text-white">
                {OPERATION_LABELS[selectedRecommendation.operationType] || selectedRecommendation.operationType}
              </span>
            </div>

            <p className="text-surface-300">{selectedRecommendation.reason}</p>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-surface-800/50 rounded-lg">
                <h4 className="text-sm text-surface-400 mb-2">Configuração Atual</h4>
                <div className="space-y-1">
                  <p className="text-white">
                    Taxa Fixa: {formatCurrency(selectedRecommendation.currentFee.fixedFee)}
                  </p>
                  <p className="text-white">
                    Taxa %: {selectedRecommendation.currentFee.percentageFee}%
                  </p>
                </div>
              </div>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <h4 className="text-sm text-green-400 mb-2">Configuração Sugerida</h4>
                <div className="space-y-1">
                  <p className="text-white">
                    Taxa Fixa: {formatCurrency(selectedRecommendation.recommendedFee.fixedFee)}
                  </p>
                  <p className="text-white">
                    Taxa %: {selectedRecommendation.recommendedFee.percentageFee}%
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="ghost" onClick={() => setSelectedRecommendation(null)}>
                Fechar
              </Button>
              <Button>
                Aplicar Recomendação
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default FeeHealthCheck;
